import * as React from "react";
import { useMemo, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface CheckboxOption {
  value: string;
  label: string;
  /** Population count shown to the right of the label. */
  count?: number;
  /** Rich label, when a plain string can't carry the row — e.g. an item name
   *  plus a muted recipient beneath it. `label` is still required: it is what
   *  the search box matches on and what a screen reader announces. */
  render?: React.ReactNode;
}

/**
 * Multi-select checkbox list designed to live inside a FilterChip popover.
 * Optional search input filters the visible options. Shows a small "Select
 * all / Clear" row at the top when there's anything to act on.
 */
export function CheckboxList({
  options,
  selected,
  onChange,
  searchable = false,
  maxHeight = 280,
  emptyText = "No options",
  className,
}: {
  options: CheckboxOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  searchable?: boolean;
  maxHeight?: number;
  emptyText?: string;
  className?: string;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {searchable && (
        <Input
          type="search"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-8 text-xs"
        />
      )}
      <div className="flex items-center justify-between text-[11px]">
        <button
          type="button"
          onClick={() => onChange(filtered.map((o) => o.value))}
          className="text-brand hover:underline focus-visible:outline-none focus-visible:underline"
        >
          Select all
        </button>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-muted-foreground hover:underline focus-visible:outline-none focus-visible:underline"
          >
            Clear ({selected.length})
          </button>
        )}
      </div>
      <ul
        className="flex flex-col gap-0.5 overflow-y-auto"
        style={{ maxHeight }}
      >
        {filtered.length === 0 ? (
          <li className="py-4 text-center text-xs text-muted-foreground">{emptyText}</li>
        ) : (
          filtered.map((o) => {
            const isChecked = selected.includes(o.value);
            return (
              <li key={o.value}>
                <label className="flex cursor-pointer items-center justify-between gap-2 rounded px-1.5 py-1 text-xs hover:bg-accent">
                  <span className="flex items-center gap-2">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggle(o.value)}
                    />
                    <span className="truncate">{o.render ?? o.label}</span>
                  </span>
                  {o.count != null && (
                    <span className="tabular-nums text-muted-foreground">{o.count}</span>
                  )}
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
